import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import MoleculeImage from '../MoleculeImage';
import { useCompoundFlipAnimation } from './useCompoundFlipAnimation';
import './CompoundComparison.css';

/**
 * CompoundComparison - Shows two compound structures side by side for comparison
 * Uses FLIP animation to transition from source entity positions
 * Used when similarity edges are present in evidence chains
 */
const CompoundComparison = ({ entity1, entity2, onClose, sourceRef1, sourceRef2, handleCloseRef }) => {
  const image1Ref = useRef(null);
  const image2Ref = useRef(null);
  const containerRef = useRef(null);

  const {
    isClosing,
    containerHeight,
    imagesOpacity,
    handleClose,
    renderAnimationPortal
  } = useCompoundFlipAnimation({
    sourceRef1,
    sourceRef2,
    containerRef,
    image1Ref,
    image2Ref,
    isReady: true,
    renderClone1: () => (
      <MoleculeImage
        encoding={entity1.smiles || entity1.inchi}
        entity={entity1}
        disableRotation={true}
      />
    ),
    renderClone2: () => (
      <MoleculeImage
        encoding={entity2.smiles || entity2.inchi}
        entity={entity2}
        disableRotation={true}
      />
    ),
    onClose,
    entity1Id: entity1?.id,
    entity2Id: entity2?.id
  });

  // Expose handleClose to parent via ref
  useEffect(() => {
    if (handleCloseRef) {
      handleCloseRef.current = handleClose;
    }
    return () => {
      if (handleCloseRef) {
        handleCloseRef.current = null;
      }
    };
  }, [handleClose, handleCloseRef]);

  if (!entity1 || !entity2) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="compound-comparison"
      style={{
        height: typeof containerHeight === 'number' ? `${containerHeight}px` : 'auto',
        padding: isClosing ? 0 : undefined,
        transition: isClosing ? 'height 0.4s ease-in, padding 0.4s ease-in' : 'none',
        overflow: 'hidden',
        flexShrink: 0
      }}
    >
      <button
        className="comparison-close"
        onClick={handleClose}
        aria-label="Close comparison"
        style={{
          opacity: imagesOpacity,
          transition: 'opacity 0.3s ease-in'
        }}
      >
        ×
      </button>
      <div className="comparison-container" style={{
        opacity: imagesOpacity,
        transition: 'opacity 0.3s ease-in'
      }}>
        <div className="comparison-item">
          <div className="comparison-image" ref={image1Ref}>
            <MoleculeImage
              encoding={entity1.smiles || entity1.inchi}
              entity={entity1}
              disableRotation={true}
            />
          </div>
          <div className="comparison-label">
            <div className="comparison-title">{entity1.preferredName || entity1.label}</div>
            <div className="comparison-category">{entity1.categoryName}</div>
          </div>
        </div>
        <div className="comparison-item">
          <div className="comparison-image" ref={image2Ref}>
            <MoleculeImage
              encoding={entity2.smiles || entity2.inchi}
              entity={entity2}
              disableRotation={true}
            />
          </div>
          <div className="comparison-label">
            <div className="comparison-title">{entity2.preferredName || entity2.label}</div>
            <div className="comparison-category">{entity2.categoryName}</div>
          </div>
        </div>
      </div>
      {renderAnimationPortal()}
    </div>
  );
};

CompoundComparison.propTypes = {
  entity1: PropTypes.object.isRequired,
  entity2: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceRef1: PropTypes.object,
  sourceRef2: PropTypes.object,
  handleCloseRef: PropTypes.object
};

export default CompoundComparison;